import React, {
    createContext, useCallback, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { memberIdSelector } from 'redux-store/auth/authSelectors';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { getImageMime } from 'utils';
import defaultImg from 'assets/images/avatar/AdvisorMale.jpg';
import { parseXml } from '../../utils/xmlParser';

const ManagerProfileContext = createContext<any>({ hasAccessPolicy: (policy: string) => {} });

const { Provider } = ManagerProfileContext;

type ManagerProfileProviderProps = {
    children: React.ReactNode;
    pathname: string;
};

const ManagerProfileProvider = (options: ManagerProfileProviderProps) => {
    const memberId = useSelector(memberIdSelector);
    const { i18n: { language } } = useTranslation();
    const [investmentContainerPageNumber, setInvestmentContainerPageNumber] = useState(1);

    const {
        children,
    } = options;

    const {
        data: profileResponse,
        error: profileError,
        isLoading: isProfileLoading,
    } = useGetCustomService({
        service: 'contactManagement',
        api: 'getMembersProfile',
        payload: [memberId],
        enabled: !!memberId,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
    });

    const {
        data: pictureResponse,
        isLoading: isPictureLoading,
        refetch: getMemberProfilePicture,
    } = useGetCustomService({
        service: 'memberManagement',
        api: 'getMemberPicture',
        payload: [memberId],
        enabled: !!memberId,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
    });

    const {
        data: memberInfoResponse,
        refetch: refetchMemberInfo,
    } = useGetCustomService({
        service: 'customMembersService',
        api: 'getMemberInfo',
        payload: [{ memberId }],
        enabled: !!memberId,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
    });

    const {
        data: dashboardResponse,
    } = useGetCustomService({
        service: 'customMembersService',
        api: 'getDashboardInfo',
        payload: [],
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
    });

    const {
        data: policiesResponse,
        isLoading: isPolicyLoading,
    } = useGetCustomService({
        service: 'customMembersService',
        api: 'getBackofficeAccessPolicies',
        payload: [],
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
    });

    const data = useMemo(() => {
        const profile = profileResponse?.data;
        const picture = pictureResponse?.data;

        if (!profile && !picture) {
            return { email: '', name: '', image: '' };
        }

        const imageBase64 = picture?.ImageContentBase64;

        return {
            image: imageBase64 ? `data:${getImageMime(imageBase64)};base64,${imageBase64}` : defaultImg,
            name: `${profile?.FirstName} ${profile?.LastName}`,
            firstName: profile?.FirstName,
            lastName: profile?.LastName,
            phone: profile?.Phone,
            mobile: profile?.Mobile,
            bankBranch: parseXml(profile?.Branch?.Name)?.root?.[language]?.['#text'],
            internalId: profile?.InternalUserId,
            superiorIds: profile?.SuperiorIds,
            hasProfilePicture: profile?.HasProfilePicture,
        };
    }, [profileResponse, pictureResponse, language]);

    const accessPolicies: string[] = policiesResponse?.data?.accessPolicies ?? [];
    const memberInfo = memberInfoResponse?.data ?? null;
    const dashboardInfo = dashboardResponse?.data ?? null;
    const isLoading = isProfileLoading || isPictureLoading;

    const hasAccessPolicy = useCallback((policy): boolean => accessPolicies?.includes(policy), [accessPolicies]);

    return (
        <Provider value={{
            data,
            error: profileError,
            isPolicyLoading,
            isLoading,
            accessPolicies,
            hasAccessPolicy,
            getMemberInfo: refetchMemberInfo,
            getMemberProfilePicture,
            memberInfo,
            dashboardInfo,
            investmentContainerPageNumber,
            setInvestmentContainerPageNumber,
        }}
        >
            {children}
        </Provider>
    );
};

export {
    ManagerProfileProvider,
    ManagerProfileContext,
};
